// src/pages/BookingConfirmation.js
import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  CheckCircle,
  AlertCircle,
  Download,
  DollarSign,
  FileText,
  Loader2,
  MapPin,
  Car,
  Utensils,
  CreditCard,
  Receipt,
  Star,
  Edit,
  Printer,
  Share2,
  Copy,
  Check,
} from "lucide-react";

const BookingConfirmation = () => {
  const { bookingNumber } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [downloadingCalendar, setDownloadingCalendar] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const email = searchParams.get("email");
    if (email && bookingNumber) {
      fetchBooking(email);
    } else {
      setShowEmailForm(true);
      setLoading(false);
    }
  }, [bookingNumber, searchParams]);

  const fetchBooking = async (email) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/bookings/${bookingNumber}?email=${email}`
      );
      setBooking(response.data);
      setError("");
    } catch (error) {
      console.error("Error fetching booking:", error);
      setError(error.response?.data?.error || "Booking not found");
      setBooking(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (emailInput) {
      fetchBooking(emailInput);
      setShowEmailForm(false);
    }
  };

  const downloadCalendarEvent = async () => {
    try {
      setDownloadingCalendar(true);
      const response = await axios.get(
        `/api/bookings/${bookingNumber}/calendar?email=${booking.customerInfo.email}`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "text/calendar" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `booking-${bookingNumber}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading calendar event:", error);
    } finally {
      setDownloadingCalendar(false);
    }
  };

  const copyBookingLink = async () => {
    const link = `${window.location.origin}/booking-confirmation/${bookingNumber}?email=${booking.customerInfo.email}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
    }
  };

  const printBooking = () => {
    window.print();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "text-green-600 bg-green-100 border-green-200";
      case "pending":
        return "text-yellow-600 bg-yellow-100 border-yellow-200";
      case "completed":
        return "text-blue-600 bg-blue-100 border-blue-200";
      case "cancelled":
        return "text-red-600 bg-red-100 border-red-200";
      case "no_show":
        return "text-gray-600 bg-gray-100 border-gray-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "paid":
        return "text-green-600 bg-green-100 border-green-200";
      case "partially_paid":
        return "text-yellow-600 bg-yellow-100 border-yellow-200";
      case "pending":
        return "text-orange-600 bg-orange-100 border-orange-200";
      case "failed":
        return "text-red-600 bg-red-100 border-red-200";
      case "refunded":
        return "text-gray-600 bg-gray-100 border-gray-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "driving_class":
        return <Car className="w-5 h-5" />;
      case "restaurant":
        return <Utensils className="w-5 h-5" />;
      default:
        return <Calendar className="w-5 h-5" />;
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (showEmailForm) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm border p-6">
          <div className="text-center mb-6">
            <FileText className="w-12 h-12 text-blue-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Booking Lookup
            </h1>
            <p className="text-gray-600">
              Enter your email address to view booking details for #
              {bookingNumber}
            </p>
          </div>

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your email address"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              View Booking
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Booking Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            {error || "We couldn't find a booking with the provided details."}
          </p>
          <div className="space-x-4">
            <button
              onClick={() => setShowEmailForm(true)}
              className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate("/")}
              className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 print:mb-6">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4 print:w-12 print:h-12" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2 print:text-2xl">
            Booking Confirmation
          </h1>
          <p className="text-gray-600">
            Here are the details for your booking #{booking.bookingNumber}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-8 print:hidden">
          <button
            onClick={downloadCalendarEvent}
            disabled={downloadingCalendar}
            className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50"
          >
            {downloadingCalendar ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>Add to Calendar</span>
          </button>

          <button
            onClick={copyBookingLink}
            className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          >
            {copied ? (
              <Check className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            <span>{copied ? "Copied!" : "Copy Link"}</span>
          </button>

          <button
            onClick={printBooking}
            className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
          >
            <Printer className="w-4 h-4" />
            <span>Print</span>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Booking Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Service Information */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-center space-x-3 mb-4">
                {getCategoryIcon(booking.service.category)}
                <h2 className="text-xl font-semibold">Service Details</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">
                    {booking.service.name}
                  </h3>
                  {booking.service.description && (
                    <p className="text-gray-600 text-sm mt-1">
                      {booking.service.description}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-sm">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <div>
                        <span className="font-medium">Date:</span>
                        <div className="text-gray-600">
                          {formatDate(booking.bookingDate)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 text-sm">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <div>
                        <span className="font-medium">Time:</span>
                        <div className="text-gray-600">
                          {formatTime(booking.startTime)} -{" "}
                          {formatTime(booking.endTime)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-sm">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <div>
                        <span className="font-medium">Duration:</span>
                        <div className="text-gray-600">
                          {booking.duration} minutes
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 text-sm">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <div>
                        <span className="font-medium">Category:</span>
                        <div className="text-gray-600 capitalize">
                          {booking.service.category.replace("_", " ")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <User className="w-5 h-5 mr-2" />
                Customer Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Full Name
                  </label>
                  <p className="text-gray-900">
                    {booking.customerInfo.firstName}{" "}
                    {booking.customerInfo.lastName}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Email
                  </label>
                  <p className="text-gray-900 flex items-center">
                    <Mail className="w-4 h-4 mr-1 text-gray-400" />
                    {booking.customerInfo.email}
                  </p>
                </div>

                {booking.customerInfo.phone && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Phone
                    </label>
                    <p className="text-gray-900 flex items-center">
                      <Phone className="w-4 h-4 mr-1 text-gray-400" />
                      {booking.customerInfo.phone}
                    </p>
                  </div>
                )}

                {booking.customerInfo.company && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Company
                    </label>
                    <p className="text-gray-900">
                      {booking.customerInfo.company}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Information */}
            {booking.formResponses && booking.formResponses.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Additional Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {booking.formResponses.map((response, index) => (
                    <div key={index}>
                      <label className="text-sm font-medium text-gray-500">
                        {response.label || response.fieldId}
                      </label>
                      <p className="text-gray-900">
                        {Array.isArray(response.value)
                          ? response.value.join(", ")
                          : response.value || "N/A"}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {(booking.notes || booking.adminNotes) && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold mb-4">Notes</h2>

                {booking.notes && (
                  <div className="mb-3">
                    <label className="text-sm font-medium text-gray-500">
                      Customer Notes
                    </label>
                    <p className="text-gray-900">{booking.notes}</p>
                  </div>
                )}

                {booking.adminNotes && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Admin Notes
                    </label>
                    <p className="text-gray-900">{booking.adminNotes}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">Booking Status</h3>

              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Status
                  </label>
                  <div
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(
                      booking.status
                    )}`}
                  >
                    {booking.status.charAt(0).toUpperCase() +
                      booking.status.slice(1).replace("_", " ")}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Booking Number
                  </label>
                  <p className="text-gray-900 font-mono">
                    {booking.bookingNumber}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Created
                  </label>
                  <p className="text-gray-900">
                    {new Date(booking.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            {booking.pricing.totalAmount > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <CreditCard className="w-5 h-5 mr-2" />
                  Payment Details
                </h3>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Payment Status
                    </label>
                    <div
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getPaymentStatusColor(
                        booking.paymentStatus
                      )}`}
                    >
                      {booking.paymentStatus.charAt(0).toUpperCase() +
                        booking.paymentStatus.slice(1).replace("_", " ")}
                    </div>
                  </div>

                  {booking.paymentMethod && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Payment Method
                      </label>
                      <p className="text-gray-900 capitalize">
                        {booking.paymentMethod}
                      </p>
                    </div>
                  )}

                  {booking.paymentDate && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Payment Date
                      </label>
                      <p className="text-gray-900">
                        {new Date(booking.paymentDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Price Breakdown */}
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium mb-3 flex items-center">
                    <Receipt className="w-4 h-4 mr-2" />
                    Price Breakdown
                  </h4>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Base Price:</span>
                      <span>${booking.pricing.basePrice.toFixed(2)}</span>
                    </div>

                    {booking.pricing.optionPrices > 0 && (
                      <div className="flex justify-between">
                        <span>Options:</span>
                        <span>${booking.pricing.optionPrices.toFixed(2)}</span>
                      </div>
                    )}

                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>${booking.pricing.subtotal.toFixed(2)}</span>
                    </div>

                    {booking.pricing.gst > 0 && (
                      <div className="flex justify-between">
                        <span>GST (5%):</span>
                        <span>${booking.pricing.gst.toFixed(2)}</span>
                      </div>
                    )}

                    {booking.pricing.pst > 0 && (
                      <div className="flex justify-between">
                        <span>PST (7%):</span>
                        <span>${booking.pricing.pst.toFixed(2)}</span>
                      </div>
                    )}

                    <div className="flex justify-between font-bold text-base border-t pt-2">
                      <span>Total:</span>
                      <span>${booking.pricing.totalAmount.toFixed(2)}</span>
                    </div>

                    {booking.pricing.depositPaid > 0 &&
                      booking.pricing.remainingAmount > 0 && (
                        <>
                          <div className="flex justify-between text-green-600">
                            <span>Paid:</span>
                            <span>
                              ${booking.pricing.depositPaid.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-orange-600">
                            <span>Remaining:</span>
                            <span>
                              ${booking.pricing.remainingAmount.toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border p-6 print:hidden">
              <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>

              <div className="space-y-3">
                <button
                  onClick={() => navigate("/")}
                  className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 flex items-center justify-center space-x-2"
                >
                  <span>Book Another Service</span>
                </button>

                {booking.status === "confirmed" &&
                  booking.service.category === "driving_class" && (
                    <button className="w-full bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 flex items-center justify-center space-x-2">
                      <Star className="w-4 h-4" />
                      <span>Rate Service</span>
                    </button>
                  )}

                <button
                  onClick={() => setShowEmailForm(true)}
                  className="w-full bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 flex items-center justify-center space-x-2"
                >
                  <Edit className="w-4 h-4" />
                  <span>Look Up Another Booking</span>
                </button>
              </div>
            </div>

            {/* Contact Information */}
            {booking.service.category === "driving_class" && (
              <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
                <h3 className="text-lg font-semibold mb-3 text-blue-900">
                  Important Information
                </h3>
                <div className="text-sm text-blue-800 space-y-2">
                  <p>• Please arrive 10 minutes early for your lesson</p>
                  <p>• Bring a valid driver's license or learner's permit</p>
                  <p>• Wear comfortable, closed-toe shoes</p>
                  <p>• Contact us at least 24 hours in advance to reschedule</p>
                </div>
              </div>
            )}

            {booking.service.category === "restaurant" && (
              <div className="bg-green-50 rounded-lg border border-green-200 p-6">
                <h3 className="text-lg font-semibold mb-3 text-green-900">
                  Reservation Notes
                </h3>
                <div className="text-sm text-green-800 space-y-2">
                  <p>
                    • Table will be held for 15 minutes past reservation time
                  </p>
                  <p>• Please inform us of any dietary restrictions</p>
                  <p>• Large parties may have time limits during peak hours</p>
                  <p>• Call ahead for cancellations or changes</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Information */}
        <div className="mt-8 pt-6 border-t border-gray-200 print:mt-6">
          <div className="bg-gray-50 rounded-lg p-6 print:bg-white print:border print:border-gray-200">
            <h3 className="text-lg font-semibold mb-3">Need Help?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  Contact Information
                </h4>
                <div className="space-y-1 text-gray-600">
                  <p className="flex items-center">
                    <Phone className="w-4 h-4 mr-2" />
                    {process.env.REACT_APP_COMPANY_PHONE || "(555) 123-4567"}
                  </p>
                  <p className="flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    {process.env.REACT_APP_COMPANY_EMAIL ||
                      "support@company.com"}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  Business Hours
                </h4>
                <div className="space-y-1 text-gray-600">
                  <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                  <p>Saturday: 9:00 AM - 4:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                This confirmation was generated on {new Date().toLocaleString()}
                . Please keep this for your records.
              </p>
            </div>
          </div>
        </div>

        {/* Print Styles */}
        <style jsx>{`
          @media print {
            @page {
              margin: 0.5in;
            }

            body {
              font-size: 12px;
              line-height: 1.4;
            }

            .print\\:hidden {
              display: none !important;
            }

            .print\\:bg-white {
              background-color: white !important;
            }

            .print\\:border {
              border: 1px solid #d1d5db !important;
            }

            .print\\:border-gray-200 {
              border-color: #e5e7eb !important;
            }

            .print\\:text-2xl {
              font-size: 1.5rem !important;
            }

            .print\\:w-12 {
              width: 3rem !important;
            }

            .print\\:h-12 {
              height: 3rem !important;
            }

            .print\\:mb-6 {
              margin-bottom: 1.5rem !important;
            }

            .print\\:mt-6 {
              margin-top: 1.5rem !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default BookingConfirmation;
